import { CloudUploadOutlined } from '@ant-design/icons'
import { Button, DatePicker, Form, Input, message, Select, Upload } from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import ContentBlockWrapper from '../../../components/ContentBlockWrapper'
import PageTitle from '../../../components/PageTitle'
import { DATE_FORMAT_LIST } from '../../../constants'
import { Container } from '../../../layouts/Container/Container'
import DashboardLayout from '../../../layouts/DashboardLayout'
import validator from '../../../validator'
import {
  DraggerUploadStyled,
  FormActionFooter,
  ViewDetailContent,
} from './DocumentVBPCUpdateStyled'

const DocumentVBPCFormPage = props => {
  const { Option } = Select
  const { Dragger } = Upload
  const {
    loadingAnimationStore,
    documentStore,
    match,
    fileStore,
    history,
  } = props

  const [form] = Form.useForm()

  const [fileList, setFileList] = useState([])

  const { documentId } = match.params

  const handleAddFileToUpload = () => false
  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  }

  const handleChangeFile = useCallback(
    info => {
      form.setFieldsValue({
        file_id: info.fileList,
      })
      setFileList(info.fileList)
    },
    [form]
  )

  useEffect(() => {
    loadingAnimationStore.showSpinner(true)
    documentStore.getVBPCDetail(documentId).then(
      res => {
        loadingAnimationStore.showSpinner(false)

        const attachments = res.data.attachments || []

        form.setFieldsValue({
          document_name: res.data.document_name,
          document_number: res.data.document_number,
          contruction_board: res.data.contruction_board,
          issue_date: moment(res.data.issue_date),
          status: res.data.status,
          issue_level: res.data.issue_level,
          file_id: attachments.map(file => ({
            uid: file.id,
            id: file.id,
            name: file.name,
            status: 'done',
            type: 'application/pdf',
          })),
        })

        setFileList(
          attachments.map(file => ({
            uid: file.id,
            id: file.id,
            name: file.name,
            status: 'done',
            type: 'application/pdf',
          }))
        )
      },
      error => {
        message.error(error.vi)
        loadingAnimationStore.showSpinner(false)
      }
    )
    return () => documentStore.clearDocumentDetail()
  }, [documentId, documentStore, loadingAnimationStore, form])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSubmitForm = async values => {
    loadingAnimationStore.showSpinner(true)
    if (values.issue_date) {
      values.issue_date = values.issue_date
        .hour(12)
        .minute(0)
        .second(0)
        .toISOString()
    }

    const existingFiles = (values.file_id || []).filter(
      file => !file.originFileObj
    )
    const newFiles = (values.file_id || []).filter(file => file.originFileObj)

    if (newFiles.length > 0) {
      const uploadPromises = newFiles.map(file => {
        const formData = new FormData()
        formData.append('file', file.originFileObj)
        return fileStore.uploadFile(formData)
      })

      Promise.all(uploadPromises)
        .then(responses => {
          const newFileIds = responses.map(response => response.data.file_id)
          values.file_id = [
            ...existingFiles.map(file => file.id),
            ...newFileIds,
          ]
          handleUpdateVBPC(documentId, values)
          loadingAnimationStore.showSpinner(false)
        })
        .catch(error => {
          message.error(error.vi)
          loadingAnimationStore.showSpinner(false)
        })
    } else {
      // not update new file
      values.file_id = existingFiles.map(file => file.id)
      handleUpdateVBPC(documentId, values)
      loadingAnimationStore.showSpinner(false)
    }
  }

  const handleUpdateVBPC = useCallback(
    (documentId, submitData) => {
      documentStore
        .updateVBPC(documentId, submitData)
        .then(() => {
          loadingAnimationStore.showSpinner(false)
          message.success(
            `Cập nhật văn bản pháp chế ${submitData.document_name} thành công`
          )
          history.push(`/utility/vbpc/view/${documentId}`)
        })
        .catch(error => {
          loadingAnimationStore.showSpinner(false)
          message.error(error.vi)
        })
    },
    [documentStore, loadingAnimationStore]
  )

  const handleCancel = () => {
    form.resetFields()
    history.push(`/utility/vbpc/view/${documentId}`)
  }

  const renderFormContent = () => (
    <ContentBlockWrapper>
      <Container maxWidth={820}>
        <Form
          scrollToFirstError={true}
          {...layout}
          form={form}
          name={'add-vbpc-form'}
          onFinish={handleSubmitForm}>
          <Form.Item
            name={'document_name'}
            label={'Tên văn bản'}
            rules={[
              { required: true, message: 'Vui lòng nhập tên tài liệu!' },
              { validator: validator.validateInputString },
            ]}>
            <Input placeholder={'Nhập tên tài liệu'} />
          </Form.Item>

          <Form.Item
            name={'document_number'}
            label={'Số quyết định ban hành'}
            rules={[
              {
                required: true,
                message: 'Vui lòng nhập số quyết định ban hành!',
              },
              { validator: validator.validateInputString },
            ]}>
            <Input placeholder={'Nhập số quyết định ban hành'} />
          </Form.Item>

          <Form.Item
            name={'issue_date'}
            label={'Ngày ban hành'}
            rules={[
              {
                required: true,
                message: 'Vui lòng nhập số quyết định ban hành!',
              },
            ]}>
            <DatePicker style={{ width: '100%' }} format={DATE_FORMAT_LIST} />
          </Form.Item>

          <Form.Item name={'issue_level'} label={'Cấp ban hành'}>
            <Input placeholder={'Nhập cấp ban hành'} />
          </Form.Item>

          <Form.Item name={'contruction_board'} label={'Ban chủ trì xây dựng'}>
            <Input placeholder={'Nhập ban chủ trì xây dựng'} />
          </Form.Item>

          <Form.Item name={'status'} label={'Trạng thái'}>
            <Select
              showSearch
              placeholder={'Trạng thái'}
              filterOption={true}
              optionFilterProp={'name'}>
              {['Đã bãi bỏ', 'Hết hiệu lực', 'Còn hiệu lực'].map(
                (status, index) => {
                  return (
                    <Option key={index} name={status} value={index + 1 + ''}>
                      {status}
                    </Option>
                  )
                }
              )}
            </Select>
          </Form.Item>

          <Form.Item
            name={'file_id'}
            label={'Tài liệu đính kèm'}
            rules={[{ required: true, message: 'Vui lòng thêm tài liệu!' }]}>
            <Dragger
              multiple
              valuePropName={'fileList'}
              fileList={fileList}
              onChange={handleChangeFile}
              beforeUpload={handleAddFileToUpload}>
              <DraggerUploadStyled>
                <CloudUploadOutlined />
                <span>Kéo thả hoặc click vào để tải lên tài liệu</span>
              </DraggerUploadStyled>
            </Dragger>
          </Form.Item>

          <FormActionFooter>
            <Button onClick={handleCancel}>Huỷ bỏ</Button>
            <Button
              style={{ marginLeft: 10 }}
              type={'primary'}
              htmlType={'submit'}>
              Cập nhật văn bản
            </Button>
          </FormActionFooter>
        </Form>
      </Container>
    </ContentBlockWrapper>
  )

  return (
    <DashboardLayout>
      <Helmet>
        <title>Chi tiết văn bản pháp chế | Tài liệu</title>
      </Helmet>
      <PageTitle
        location={props.location}
        title={'Chi tiết văn bản pháp chế'}
        routerGoback={'/utility/vbpc'}
      />
      <ViewDetailContent>{renderFormContent()}</ViewDetailContent>
    </DashboardLayout>
  )
}

DocumentVBPCFormPage.propTypes = {}

export default memo(
  inject(
    'loadingAnimationStore',
    'commonStore',
    'documentStore',
    'fileStore'
  )(observer(DocumentVBPCFormPage))
)
